// App.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import CinemaMap from './components/Map';
import SearchBar from './components/Search';
import Filter from './components/Filter';
import MovieRow from './components/Movie/MovieRow';
import MovieCard from './components/Movie/MovieCard';
import MovieDetail from './components/Movie/MovieDetail';
import MovieSkeleton from './components/Movie/MovieSkeleton';
import { useCinemas } from './hooks/useCinemas';
import { useMovies } from './hooks/useMovies';
import { Drawer } from 'vaul';

const ITEMS_PER_PAGE = 12; // Number of movies to show initially and per scroll
const GRID_ITEM_WIDTH = 200; // Approximate width of a grid item
const GRID_ITEM_HEIGHT = 200; // Approximate height of a grid item
const ROW_ITEM_HEIGHT = 120; // Approximate height of a row item
const SCROLL_THRESHOLD = 0.8;
const SCROLL_DEBOUNCE = 150;

const App = () => {
    const { cinemas, loading: loadingCinemas, error: errorCinemas } = useCinemas();
    const { movies, loading: loadingMovies, error: errorMovies } = useMovies();
    const [isMobile, setIsMobile] = useState(false);
    const [open, setOpen] = useState(true);
    const [view, setView] = useState("grid");
    const [selectedMovie, setSelectedMovie] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [activeFilters, setActiveFilters] = useState({
        days: [],
        timeSlots: [],
        passes: [],
        types: []
    });
    const contentRef = useRef(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const [visibleMovies, setVisibleMovies] = useState(ITEMS_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false);
    const loadingTimeoutRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    const viewportHeight = window.innerHeight;
    const snapPoints = [0.3 * viewportHeight + 'px', 0.8 * viewportHeight + 'px'];
    const remainingHeight = 0.2 * viewportHeight + 'px';
    const [snap, setSnap] = useState(snapPoints[0]);

    const handleSearch = ({ query, filters }) => {
        setSearchQuery(query);
        setActiveFilters(filters);
    };

    const handleTouchStart = (e) => {
        if (contentRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            const isAtTop = scrollTop === 0;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
            
            if (!isAtTop && !isAtBottom) {
                setIsScrolling(true);
            }
        }
    };

    const handleTouchEnd = () => {
        setIsScrolling(false);
    };

    const handleTouchMove = (e) => {
        if (contentRef.current && isScrolling) {
            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            const isAtTop = scrollTop === 0;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
            
            if ((isAtTop && e.touches[0].clientY > 0) || (isAtBottom && e.touches[0].clientY < 0)) {
                e.stopPropagation();
            }
        }
    };

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const validCinemas = cinemas.filter(cinema =>
        cinema.coordinates.lat != null && cinema.coordinates.lon != null
    );

    const filteredMovies = useMemo(() => {
        return movies.filter(movie => {
            // Filtre par recherche textuelle
            const matchesSearch = searchQuery === "" || 
                movie.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                movie.director.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                movie.director.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                movie.actors.some(actor => 
                    actor.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    actor.lastName.toLowerCase().includes(searchQuery.toLowerCase())
                );

            // Filtre par type de film
            const matchesType = activeFilters.types.length === 0 || 
                (activeFilters.types.includes('new') && movie.isNew) ||
                (activeFilters.types.includes('classic') && movie.isClassic);

            // Filtre par carte d'abonnement
            const matchesPass = activeFilters.passes.length === 0 || 
                Object.keys(movie.showtimes).some(cinemaId => {
                    const cinema = cinemas.find(c => c.allocine_id === cinemaId);
                    return cinema && activeFilters.passes.includes(cinema.pass_type);
                });

            // Filtre par jour et horaire
            const matchesSchedule = activeFilters.days.length === 0 && activeFilters.timeSlots.length === 0 || 
                Object.values(movie.showtimes).some(versions => 
                    Object.values(versions).some(times => 
                        times.some(time => {
                            const [hours, minutes] = time.split(':').map(Number);
                            const date = new Date();
                            date.setHours(hours, minutes);
                            
                            const dayMatch = activeFilters.days.length === 0 || 
                                activeFilters.days.includes(date.toLocaleDateString('fr-FR', { weekday: 'short' }).toLowerCase());
                            
                            const timeMatch = activeFilters.timeSlots.length === 0 || 
                                activeFilters.timeSlots.some(slot => {
                                    const [start, end] = slot.split('-').map(t => {
                                        const [h] = t.split('h').map(Number);
                                        return h;
                                    });
                                    return hours >= start && hours < end;
                                });
                            
                            return dayMatch && timeMatch;
                        })
                    )
                );

            return matchesSearch && matchesType && matchesPass && matchesSchedule;
        });
    }, [movies, searchQuery, activeFilters, cinemas]);

    const loadMoreMovies = useCallback(() => {
        if (isLoading || visibleMovies >= filteredMovies.length) return;
        
        setIsLoading(true);
        
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        loadingTimeoutRef.current = setTimeout(() => {
            setVisibleMovies(prev => {
                const newCount = Math.min(prev + ITEMS_PER_PAGE, filteredMovies.length);
                setIsLoading(false);
                return newCount;
            });
        }, 300);
    }, [isLoading, visibleMovies, filteredMovies.length]);

    // Handle scroll events
    const handleScroll = useCallback((e) => {
        if (!contentRef.current || isLoading) return;
        
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
        
        if (scrollPercentage > SCROLL_THRESHOLD && visibleMovies < filteredMovies.length) {
            loadMoreMovies();
        }
    }, [loadMoreMovies, isLoading, visibleMovies, filteredMovies.length]);

    // Cleanup timeout
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    // Reset visible movies when filters change
    useEffect(() => {
        setVisibleMovies(ITEMS_PER_PAGE);
        setIsLoading(false);
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [searchQuery, activeFilters]);

    const handleMapLoaded = () => {
        console.log('Map loaded in App');
        setIsMapLoaded(true);
    };

    const handleMovieSelect = (movie) => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
        setSelectedMovie(movie);
    };

    const handleBackToList = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
        setSelectedMovie(null);
    };

    if (errorCinemas || errorMovies) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="text-red-500 text-center">
                    <p className="text-2xl mb-2">⚠️</p>
                    <p>{errorCinemas || errorMovies}</p>
                </div>
            </div>
        );
    }

    const renderMovieList = () => {
        const visibleMoviesList = filteredMovies.slice(0, visibleMovies);
        
        if (visibleMoviesList.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                    <div className="bg-gray-100 rounded-lg p-6 max-w-sm">
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                            Aucun film trouvé
                        </h3>
                        <p className="text-gray-600">
                            {searchQuery ? (
                                <>Aucun film ne correspond à votre recherche "{searchQuery}"</>
                            ) : (
                                "Essayez de modifier vos filtres pour voir plus de résultats"
                            )}
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <>
                {view === "grid" ? (
                    <div className="grid grid-cols-3 gap-3 pb-4">
                        {visibleMoviesList.map((movie) => (
                            <MovieCard 
                                key={movie.id} 
                                movie={movie}
                                onClick={() => handleMovieSelect(movie)} 
                            />
                        ))}
                        {isLoading && Array(6).fill(0).map((_, index) => (
                            <MovieSkeleton key={`skeleton-${index}`} />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 pb-4">
                        {visibleMoviesList.map((movie) => (
                            <MovieRow 
                                key={movie.id} 
                                movie={movie}
                                onClick={() => handleMovieSelect(movie)} 
                            />
                        ))}
                        {isLoading && Array(3).fill(0).map((_, index) => (
                            <MovieSkeleton key={`skeleton-${index}`} isRow={true} />
                        ))}
                    </div>
                )}
            </>
        );
    };

    return (
        <div style={{
            position: 'relative',
            height: '100vh',
            width: '100%'
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }}>
                <CinemaMap 
                    cinemas={validCinemas} 
                    onMapLoaded={handleMapLoaded}
                />
            </div>

            {isMobile && isMapLoaded && (
                <Drawer.Root 
                    modal={false} 
                    dismissible={false} 
                    open={open} 
                    onOpenChange={setOpen} 
                    snapPoints={snapPoints} 
                    activeSnapPoint={snap} 
                    setActiveSnapPoint={setSnap}
                    shouldScaleBackground={false}
                >
                    <Drawer.Portal>
                        <Drawer.Overlay className="fixed inset-0 bg-black/40" />
                        <Drawer.Content
                            className="bg-white fixed bottom-0 left-0 right-0 outline-none"
                            style={{
                                height: '90vh',
                                zIndex: 9999,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Drawer.Title className="sr-only">Liste des films</Drawer.Title>
                            <div
                                style={{
                                    height: '25px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'grab',
                                }}
                            >
                                <div
                                    style={{
                                        width: '48px',
                                        height: '6px',
                                        backgroundColor: '#c5c7c5',
                                        borderRadius: '9999px',
                                    }}
                                />
                            </div>

                            <div className="px-3 shrink-0">
                                <SearchBar onSearch={setSearchQuery} />
                                <div className="mt-4">
                                    <Filter
                                        onViewChange={setView}
                                        selectedMovie={selectedMovie}
                                        onBack={handleBackToList}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>

                            <div 
                                ref={contentRef}
                                className="px-3 flex-1 overflow-y-auto overscroll-contain"
                                style={{
                                    touchAction: 'pan-y',
                                    WebkitOverflowScrolling: 'touch',
                                    paddingBottom: remainingHeight
                                }}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                                onTouchMove={handleTouchMove}
                                onScroll={handleScroll}
                            >
                                {selectedMovie ? (
                                    <MovieDetail movie={selectedMovie} onBack={handleBackToList} />
                                ) : renderMovieList()}
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            )}
        </div>
    );
};

export default App;